import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSession } from "../../hooks/useSession";
import { useStates } from "../../hooks/useStates";
import { useGeo } from "../../hooks/useGeo";
import urls from "../../config/urls.json";
import { isCap } from "../../services/validators";
import { ApiService as api } from "../../services/ApiService";
import { getYMD } from "../../services/formatters";
import { useEditAppointment } from "../../hooks/useEditAppointment";
import { useExternalParams } from "../../hooks/useExternalParams";
import { isProd } from "../../services/env";
import { useDynatrace } from "../../hooks/useDynatrace";

window.searchTimeout = null;

export const useLuogoPage = () => {
    const navigate = useNavigate();
    const { read, update, batchUpdate, setC5 } = useSession();
    const { searchAutocomplete, geolocalize, geoCode, geoDecode } = useGeo();
    const { states, setCurrentState, isFilialiPageReady, isFetchFilialiStart, isFetchFilialiWaiting, isFetchPositioEnd, isFetchPositioStart } = useStates();
    const { showModal, closeModal, askForEditAppointment } = useEditAppointment();

    const [suggestions, setSuggestions] = useState([]);
    const [fetchError, setFetchError] = useState(false);
    const [fetchErrorMessage, setFetchErrorMessage] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [soloInfo, setSoloInfo] = useState("");

    // from useLoading
    const { handleExternalParams } = useExternalParams();

    useEffect(() => {
        // update("prodotto", 1);
        update("luogo", "");
        setCurrentState(states.CHECK_EXT_PARAMS_START);
        checkForExternalParams();
    }, []);

    const {} = useDynatrace();

    const redirectTo = (path) => {
        update("redirect", "");
        window.setTimeout(() => {
            navigate(path);
        }, 10);
    };

    const setPrestitoBusiness = () => {
        update("prodotto", 5);
        navigate(urls.prestitoBusiness.path);
    };

    const checkForExternalParams = async () => {
        const extParamsResultCallback = await handleExternalParams();
        if (extParamsResultCallback) {
            await extParamsResultCallback();
            return;
        }

        setCurrentState(states.CHECK_EXT_PARAMS_END);
        // const url = urls.luogo.path;
        // navigate(url, { replace: true });
        if (read("oppID_external")) {
            askForEditAppointment();
        }
        setIsLoading(false);
    };

    const next = () => {
        if (isFilialiPageReady()) {
            window.dataLayer.push({
                event: "presa_info_fa",
                eventCategory: "Fissa Appuntamento",
                eventAction: "Presa informazioni",
                eventLabel: read("solo_info"),
            });
            navigate(urls.filiali.path);
        } else {
            setCurrentState(states.FETCH_FILIALI_WAITING);
        }
    };

    const onGeoClick = async () => {
        setSuggestions([]);

        setCurrentState(states.FETCH_POSITION_START);
        const geoResult = await geolocalize();
        update("luogo", geoResult.address);

        // we also need cap for TrovaFiliali3
        let decodeGeoResult = {};
        try {
            decodeGeoResult = await geoDecode(geoResult.coords);
        } catch (error) {
            setFetchErrorMessage("C'è stato un errore nella ricerca delle filiali. <br />Per favore riprova.");
            setFetchError(true);
            return;
        }

        update("cap", decodeGeoResult.postCode);

        _onInputReceived(geoResult.coords);
    };

    const _onInputReceived = async (coords = null, suggestionCoords = null, placeType = "") => {
        update("placeType", placeType);

        let coordinate = coords;

        if (null == coordinate) {
            setCurrentState(states.FETCH_POSITION_START);
            const searchString = read("luogo");
            // 1. ottenere le coordinate
            if (isCap(searchString)) {
                // se cap -> translatecaptocoords
                try {
                    coordinate = await api.getCoordsByCap(searchString);
                } catch (error) {
                    setFetchErrorMessage("C'è stato un errore nella ricerca delle filiali. <br />Per favore riprova.");
                    setFetchError(true);
                    return;
                }
                update("cap", searchString);
            } else {
                // se non cap -> geocode [cap ok]
                try {
                    coordinate = await geoCode(searchString);
                    if (coordinate.address.toLowerCase() == "italia") throw new Error();
                } catch (error) {
                    if (!isProd()) console.log("error");
                    // setFetchErrorMessage("C'è stato un errore nella ricerca delle filiali. <br />Per favore riprova.");
                    // setFetchError(true);
                    setCurrentState(states.FETCH_POSITION_ERROR);
                    return;
                }

                // test
                // if (!coordinate.cap) {
                //     const res = await geoDecode(suggestionCoords || { lat: coordinate.lat, lng: coordinate.lng });
                //     coordinate.cap = res.postCode;
                //     update("placeType", "LOCALITY");
                // }
                //-- test

                update("cap", coordinate.cap || coordinate.city);

                if (!read("luogo").match(/ Italia$/)) {
                    update("luogo", coordinate.address);
                }
            }
        }
        setCurrentState(states.FETCH_POSITION_END);

        // 2. trovafiliali con coordinate e cap (?)
        setCurrentState(states.FETCH_FILIALI_START);
        let filialiResult = {};
        try {
            filialiResult = await api.TrovaFiliali3({
                cap: read("cap"),
                latitude: coordinate.lat,
                longitude: coordinate.lng,
                city: coordinate.locality,
                placeType: read("placeType") || "",
            });
        } catch (error) {
            setFetchErrorMessage("C'è stato un errore nella ricerca delle filiali. <br />Per favore riprova.");
            setFetchError(true);
            return;
        }

        batchUpdate({
            filiali: filialiResult.filiali,
            filialeScelta: filialiResult.filiali[0],
            disponibilita: filialiResult.filiali[0].days,
            dataScelta: filialiResult.filiali[0].days[0],
            ricontattoLuogo: filialiResult.ricontatto,
        });

        // 3. chiedere disponibilita per prima filiale e primo giorno
        let disponibilitaResult = {};
        try {
            // disponibilitaResult = await api.Disponibilita({
            //   CodFiliale: read("filiali")[0].codFiliale,
            //   Giorno: getYMD(read("filiali")[0].days[0].data),
            // });
            disponibilitaResult = filialiResult.filiali[0];
        } catch (error) {
            setFetchErrorMessage("C'è stato un errore nella ricerca delle filiali. <br />Per favore riprova.");
            setFetchError(true);
            return;
        }

        // update("orari", disponibilitaResult.orariDisponibili);
        // update("orarioScelto", disponibilitaResult.orariDisponibili.filter((_) => _.disponibile == true)[0]);
        batchUpdate({
            orari: disponibilitaResult.orariDisponibili,
            orarioScelto: disponibilitaResult.orariDisponibili.filter((_) => _.disponibile == true)[0],
        });

        // 1. we need it to make sure we automagically call "next" if user
        //    clicked before filiali were actually fetched
        const previousState = read("state");

        setCurrentState(states.FETCH_FILIALI_END);

        // 2. now call automagically
        if (previousState == states.FETCH_FILIALI_WAITING) next();
    };

    const onLuogoChange = async (value) => {
        window.clearTimeout(window.searchTimeout);

        setCurrentState(states.BUSY);
        update("luogo", value);

        if (value.length <= 3) {
            setSuggestions([]);
        } else {
            window.searchTimeout = window.setTimeout(async () => {
                const suggestions = await searchAutocomplete(value);
                // console.log("asd");
                setSuggestions(suggestions);
            }, 2000);
        }
    };
    const onLuogoBlur = () => {
        setTimeout(() => {
            if (read("state") >= states.FETCH_POSITION_START) return;

            if (read("luogo").replaceAll(" ", "").length == 0) return;
            _onInputReceived();
            // setSuggestions([]);
        }, 250);
    };
    const onSuggestionClick = (itm) => {
        update("luogo", itm.displayLines.join(", "));
        _onInputReceived(null, { lat: itm.coordinate.latitude, lng: itm.coordinate.longitude }, itm.placeType);
        setSuggestions([]);
    };
    const getSuggestionText = (itm) => {
        return itm.displayLines.join(", ");
    };

    const getSearchInputState = () => {
        return isFetchPositioStart() ? 2 : isFetchPositioEnd() ? 3 : 1;
    };

    return {
        read,
        update,
        next,
        onLuogoChange,
        onLuogoBlur,
        onSuggestionClick,
        getSuggestionText,
        suggestions,
        setSuggestions,
        onGeoClick,
        isFilialiPageReady,
        isFetchFilialiStart,
        isFetchFilialiWaiting,
        isFetchPositioEnd,
        isFetchPositioStart,
        getSearchInputState,
        fetchErrorMessage,
        fetchError,
        setFetchError,
        showModal,
        closeModal,
        askForEditAppointment,
        isLoading,
        setSoloInfo,
        setPrestitoBusiness,
        setC5,
        redirectTo,
    };
};
